import { useNavigate } from '@faceup/router'
import { Lottie } from '@faceup/ui'
import { Button, Col, Row } from '@faceup/ui-base'
import { ChannelType } from '@faceup/utils'
import type { ReactNode } from 'react'
import { useInstitution } from '../../hooks/useInstitution'
import { useSurveyPreview } from '../../hooks/useSurveyPreview'
import { defineMessages, FormattedMessage } from '../../TypedIntl'
import { sizeSettings } from '../../utils/constants'
import PageTitle from '../PageTitle'
import lottieCheckmark from './assets/lottie-checkmark.json'

const messages = defineMessages({
  backToHomepage: 'FollowUp.header.backToHomepage',
})

const messagesPerChannel = defineMessages<ChannelType>({
  [ChannelType.AutomatedHotline]: 'Shared.CompanyReportSent.title',
  [ChannelType.LiveHotline]: 'Shared.CompanyReportSent.title',
  [ChannelType.AiHotline]: 'Shared.CompanyReportSent.title',
  [ChannelType.ReportingSystem]: 'Shared.CompanyReportSent.title',
  [ChannelType.Survey]: 'Shared.surveys.detail.afterSendPageTitle',
})

type SentContentChannelProps = {
  children?: ReactNode
  description?: ReactNode
  sourceType: ChannelType
  title?: never
}

type SentContentTitleProps = {
  children?: ReactNode
  description?: ReactNode
  title: ReactNode
  sourceType?: never
}

type SentContentProps = SentContentChannelProps | SentContentTitleProps

export const SentContent = (props: SentContentProps) => {
  const { children, description } = props
  const institutionContext = useInstitution()
  const navigate = useNavigate()
  const { isPreview } = useSurveyPreview()

  const resolvedTitle: ReactNode = (() => {
    if ('title' in props && props.title !== undefined) {
      return props.title
    }
    if (!('sourceType' in props) || !props.sourceType) {
      return null
    }
    return <FormattedMessage {...messagesPerChannel[props.sourceType]} />
  })()

  const isSurvey = props.sourceType === undefined

  return (
    <Row justify='center' data-test='ReportSent' inert={isPreview || undefined}>
      <Col {...sizeSettings}>
        <div className='flex justify-center'>
          <div className='w-16'>
            <Lottie animationData={lottieCheckmark} shouldPlay />
          </div>
        </div>
        <PageTitle description={description}>{resolvedTitle}</PageTitle>
        <div className='flex flex-col gap-8'>
          {children}
          {institutionContext?.homepage && !isSurvey && (
            <div className='flex justify-center'>
              <Button
                onClick={() => navigate(institutionContext.homepage)}
                data-test='report-sent-button-back-to-homepage'
              >
                <FormattedMessage {...messages.backToHomepage} />
              </Button>
            </div>
          )}
        </div>
      </Col>
    </Row>
  )
}
